import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Grid,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  useMediaQuery,
} from "@mui/material";
import ShareIcon from "@mui/icons-material/Share";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import PeopleIcon from "@mui/icons-material/People";
import SchoolIcon from "@mui/icons-material/School";

import SharePodcastDialog from "./SharePodcastDialog";
import LinkIcon from "@mui/icons-material/Link";
import { useSnackbar } from "./SnackbarHandler";
import { getStyles } from "../index";
import { formatDate, availableSpeakers } from "./functions";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { useTheme } from "@mui/material/styles";
import ShareDialog from "./ShareDialog";
import LoadingBackdrop from "./LoadingBackdrop";
import Logger from "./Logger";

import { db } from "./firebase";
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  deleteDoc,
  doc,
  serverTimestamp,
} from "firebase/firestore";

const MyPodcasts = ({ user, isDark }) => {
  const [podcasts, setPodcasts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [shareAnchorEl, setShareAnchorEl] = useState(null);
  const [currentPodcast, setCurrentPodcast] = useState(null);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareDialogOpenFriends, setShareDialogOpenFriends] = useState(false);
  const [shareLinkDialogOpen, setShareLinkDialogOpen] = useState(false);

  const [isSharing, setIsSharing] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [podcastToDelete, setPodcastToDelete] = useState(null);

  const navigate = useNavigate();
  const theme = useTheme();
  const styles = getStyles(theme);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { showSnackbar } = useSnackbar();

  useEffect(() => {
    const fetchPodcasts = async () => {
      try {
        setLoading(true);
        const q = query(
          collection(db, "podcasts"),
          where("userId", "==", user.uid),
        );
        const querySnapshot = await getDocs(q);
        const podcastList = [];
        querySnapshot.forEach((doc) => {
          const podcastData = doc.data();
          // Only add podcasts where shared doesn't exist or is not true
          if (
            !podcastData.hasOwnProperty("shared") ||
            podcastData.shared !== true
          ) {
            podcastList.push({
              id: doc.id,
              ...podcastData,
            });
          }
        });
        setPodcasts(podcastList);
      } catch (error) {
        console.error("Error fetching podcasts:", error);
        showSnackbar("Ett fel uppstod när podcasten skulle hämtas.", "error");
      } finally {
        setLoading(false);
      }
    };
    if (user?.uid) {
      fetchPodcasts();
    }
  }, [user]);

  const handleShareMenuOpen = (event, podcast) => {
    event.stopPropagation();
    setCurrentPodcast(podcast);
    setShareAnchorEl(event.currentTarget);
  };

  const handleShareMenuClose = () => {
    setShareAnchorEl(null);
  };

  const handlePodcastClick = (podcastId) => {
    navigate(`/app/podcast/${podcastId}`);
  };

  const handleCreatePodcast = () => {
    navigate("/app/create-podcast");
  };

  const handleSharePodcast = async ({
    roomId,
    studentIds,
    comment,
    fileUrl,
  }) => {
    if (!currentPodcast) return;

    Logger.log("Sharing podcast:", currentPodcast);
    Logger.log("With room:", roomId);
    Logger.log("To students:", studentIds);
    Logger.log("Comment:", comment);

    setIsSharing(true);
    await new Promise((resolve) => setTimeout(resolve, 1800));

    try {
      // Create a document for each student
      const sharePromises = studentIds.map(async (studentId) => {
        const documentData = {
          sharedTo: studentId,
          comment: comment,
          fileUrl: fileUrl,
          transcriptContent: currentPodcast.transcriptContent,
          shared: true,
          userId: user.uid,
          podcastUrl: currentPodcast.podcastUrl,
          podcastName: currentPodcast.podcastName,
          createdAt: Date.now(),
          speaker1: currentPodcast.speaker1,
          speaker2: currentPodcast.speaker2,
          topic: currentPodcast.topic || "",
          originalPodcastId: currentPodcast.id,
        };

        // Create the podcast document and get its ID
        const podcastDoc = await addDoc(
          collection(db, "podcasts"),
          documentData,
        );

        Logger.log(
          "Adding document for student",
          studentId,
          ":",
          JSON.stringify(documentData, null, 2),
        );

        // Create notification with the podcast doc ID
        await addDoc(collection(db, "notifications"), {
          type: "podcast_shared",
          recipientId: studentId,
          senderId: user.uid,
          read: false,
          createdAt: serverTimestamp(),
          podcastData: {
            podcastId: podcastDoc.id,
            podcastName: documentData.podcastName,
            podcastUrl: documentData.podcastUrl,
            comment: documentData.comment,
            topic: documentData.topic,
          },
        });

        return podcastDoc;
      });

      await Promise.all(sharePromises);
      Logger.log("Podcast shared saved to DB");
      showSnackbar("Du har nu delat podcasten!", "success");

      // Close the share dialog
      setShareDialogOpen(false);
      setShareDialogOpenFriends(false);
    } catch (error) {
      Logger.error("Error sharing podcast:", error);
      showSnackbar("Ett fel uppstod när podcasten skulle delas.", "error");
    } finally {
      setIsSharing(false);
    }
  };

  const handleDeletePodcast = async () => {
    if (!podcastToDelete) return;

    try {
      await deleteDoc(doc(db, "podcasts", podcastToDelete.id));
      setPodcasts(
        podcasts.filter((podcast) => podcast.id !== podcastToDelete.id),
      );
      showSnackbar("Podcasten har tagits bort", "success");
    } catch (error) {
      console.error("Error deleting podcast:", error);
      showSnackbar("Ett fel uppstod när podcasten skulle tas bort", "error");
    } finally {
      setDeleteDialogOpen(false);
      setPodcastToDelete(null);
    }
  };

  const getSpeakerAvatar = (speakerId) => {
    const speaker = availableSpeakers.find(
      (speaker) => speaker.id === speakerId,
    );
    return speaker ? speaker.avatar : null;
  };

  return (
    <Box sx={styles.container}>
      <Box
        sx={{
          ...styles.innerContainer,
          width: { xs: "100%", md: "100%" },
        }}
      >
        <Typography
          variant="h1"
          sx={{
            ...styles.newH1,
            pl: 0,
            width: "100%",
            textAlign: "left",
          }}
        >
          Mina podcasts
        </Typography>

        <Box
          sx={{
            ...styles.buttonsContainer,
            ...styles.additionalButtonsContainer,
          }}
        >
          {podcasts.length > 0 && (
            <Button
              variant="purp"
              sx={isMobile ? styles.tightButton : {}}
              onClick={(e) => {
                e.preventDefault();
                navigate("/app/podcast");
              }}
              startIcon={<AddIcon />}
            >
              Generera podcast
            </Button>
          )}
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress sx={{ fontSize: 40 }} />
          </Box>
        ) : (
          <>
            <Grid container spacing={4} sx={{ mt: 4 }}>
              {/* Create New Podcast Box */}
              <Grid item xs={12} md={6} lg={4} sx={{ display: "none" }}>
                <Box
                  onClick={handleCreatePodcast}
                  sx={{
                    ...styles.freshBox,
                    backgroundColor: "#805ff41a",
                    boxShadow: "none",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "16px",
                    borderRadius: "8px",
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                    height: "260px",
                    "&:hover": {
                      backgroundColor: "#805ff429",
                      transform: "translateY(-4px)",
                    },
                    "&:hover .plusIcon": {
                      transform: "rotate(90deg)",
                    },
                  }}
                >
                  <IconButton
                    sx={{
                      backgroundColor: theme.palette.primary.main,
                      color: "#fff",
                      width: "64px",
                      height: "64px",
                      mb: 2,
                      transition: "transform 0.3s ease",
                    }}
                    className="plusIcon"
                  >
                    <AddIcon sx={{ fontSize: 32 }} />
                  </IconButton>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Skapa ny podcast
                  </Typography>
                </Box>
              </Grid>

              {/* Podcast Cards */}
              {podcasts.map((podcast) => (
                <Grid item xs={12} md={6} lg={4} key={podcast.id}>
                  <Box
                    onClick={() => handlePodcastClick(podcast.id)}
                    sx={{
                      ...styles.freshBox,

                      boxShadow: "none",
                      display: "flex",
                      flexDirection: "column",
                      padding: "16px",
                      borderRadius: "8px",
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                      height: "260px",
                      "&:hover": {
                        border: `1px solid ${theme.palette.primary.main}`,
                      },
                      "&:hover .avatarTransition": {
                        transform: "scale(1.1)",
                      },
                      position: "relative",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        mb: 2,
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 600, maxWidth: "80%" }}
                        noWrap
                      >
                        {podcast.podcastName || "Untitled Podcast"}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={(e) => handleShareMenuOpen(e, podcast)}
                        sx={{
                          color: theme.palette.text.secondary,
                          "&:hover": {
                            color: theme.palette.primary.main,
                          },
                        }}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </Box>

                    <Box
                      sx={{
                        flexGrow: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                        }}
                      >
                        <Avatar
                          src={getSpeakerAvatar(podcast.speaker1)}
                          alt="Speaker 1"
                          sx={{
                            width: 80,
                            height: 80,
                            transition: "transform 0.3s ease",
                            boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
                          }}
                          className="avatarTransition"
                        />
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, fontSize: "1rem" }}
                        >
                          &
                        </Typography>
                        <Avatar
                          src={getSpeakerAvatar(podcast.speaker2)}
                          alt="Speaker 2"
                          sx={{
                            width: 80,
                            height: 80,
                            transition: "transform 0.3s ease",
                            boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
                          }}
                          className="avatarTransition"
                        />
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        mt: 2,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ color: "text.secondary" }}
                      >
                        {formatDate(podcast.createdAt)}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
            {podcasts.length === 0 && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  p: 4,
                }}
              >
                <Typography
                  variant="h5"
                  sx={{ mb: 3, textAlign: "center", fontSize: "1.2rem" }}
                >
                  Du har inga podcasts ännu
                </Typography>
                <Button
                  variant="purp"
                  sx={isMobile ? styles.tightButton : {}}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate("/app/podcast");
                  }}
                  startIcon={<AddIcon />}
                >
                  Generera podcast
                </Button>
              </Box>
            )}
          </>
        )}
      </Box>

      {/* Share Menu */}
      <Menu
        anchorEl={shareAnchorEl}
        open={Boolean(shareAnchorEl)}
        onClose={handleShareMenuClose}
        PaperProps={{
          sx: {
            ...styles.roundMenu,
            ...styles.roundMenuLarger,
          },
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {user.accountType === "teacher" && (
          <MenuItem
            onClick={(e) => {
              e.stopPropagation();
              setShareDialogOpen(true);
              handleShareMenuClose();
            }}
            sx={{
              ...styles.menuItemStyle,
              ...styles.menuItemStyleLarger,
            }}
          >
            <ListItemIcon sx={{ mr: 1 }}>
              <SchoolIcon style={{ fontSize: "1.5rem" }} />
            </ListItemIcon>
            <ListItemText
              primary="Dela med elever"
              secondary="Dela med elever från ett av dina elevrum"
            />
          </MenuItem>
        )}
        <MenuItem
          onClick={(e) => {
            e.stopPropagation();
            setShareLinkDialogOpen(true);
            handleShareMenuClose();
          }}
          sx={{
            ...styles.menuItemStyle,
            ...styles.menuItemStyleLarger,
          }}
        >
          <ListItemIcon sx={{ mr: 1 }}>
            <LinkIcon style={{ fontSize: "1.5rem" }} />
          </ListItemIcon>
          <ListItemText
            primary="Dela länk"
            secondary="Kopiera en delbar länk till podcasten"
          />
        </MenuItem>
        <MenuItem
          onClick={(e) => {
            e.stopPropagation();
            setShareDialogOpenFriends(true);
            handleShareMenuClose();
          }}
          sx={{
            ...styles.menuItemStyle,
            ...styles.menuItemStyleLarger,
          }}
        >
          <ListItemIcon sx={{ mr: 1 }}>
            <PeopleIcon style={{ fontSize: "1.5rem" }} />
          </ListItemIcon>
          <ListItemText
            primary="Dela med vänner"
            secondary="Dela med vänner från din vänlista"
          />
        </MenuItem>
        <MenuItem
          onClick={(e) => {
            e.stopPropagation();
            setPodcastToDelete(currentPodcast);
            setDeleteDialogOpen(true);
            handleShareMenuClose();
          }}
          sx={{
            ...styles.menuItemStyle,
            ...styles.menuItemStyleLarger,
            color: theme.palette.error.main,
          }}
        >
          <ListItemIcon sx={{ mr: 1, color: theme.palette.error.main }}>
            <DeleteIcon style={{ fontSize: "1.5rem" }} />
          </ListItemIcon>
          <ListItemText
            primary="Ta bort podcast"
            secondary="Ta bort denna podcast permanent"
          />
        </MenuItem>
      </Menu>

      {/* Share Dialog */}
      <ShareDialog
        open={shareDialogOpen}
        onClose={() => setShareDialogOpen(false)}
        onShare={handleSharePodcast}
        user={user}
      />

      <ShareDialog
        open={shareDialogOpenFriends}
        onClose={() => setShareDialogOpenFriends(false)}
        onShare={handleSharePodcast}
        user={user}
        theme={theme}
        shareWithFriends={true}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: "12px",
            padding: "8px",
            maxWidth: "400px",
          },
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Ta bort podcast</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Är du säker på att du vill ta bort podcasten "
            {podcastToDelete?.podcastName}"? Detta går inte att ångra.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ padding: "16px" }}>
          <Button onClick={() => setDeleteDialogOpen(false)} variant="print">
            Avbryt
          </Button>
          <Button onClick={handleDeletePodcast} variant="purp" sx={{ ml: 2 }}>
            Ta bort
          </Button>
        </DialogActions>
      </Dialog>

      {/* Loading Backdrop */}
      <LoadingBackdrop
        isOpen={isSharing}
        imagePath="/img/AI.png"
        loadingText="Delar podcast..."
      />

      {/* Share Link Dialog */}
      <SharePodcastDialog
        open={shareLinkDialogOpen}
        onClose={() => setShareLinkDialogOpen(false)}
        podcastId={currentPodcast?.id}
      />
      <ShareDialog
        open={shareDialogOpen}
        onClose={() => setShareDialogOpen(false)}
        onShare={handleSharePodcast}
        user={user}
      />

      <ShareDialog
        open={shareDialogOpenFriends}
        onClose={() => setShareDialogOpenFriends(false)}
        onShare={handleSharePodcast}
        user={user}
        theme={theme}
        shareWithFriends={true}
      />
    </Box>
  );
};

export default MyPodcasts;
